import { useEffect, useMemo, useState } from "react";
import { Checkbox, Input, Spin } from "antd";
import Button from "@lhd/components/Button";
import SafeHtml from "@lhd/components/SafeHtml";
import {
  useAvailableDuplicateTargetCompanyIds,
  useDuplicateRecordsV2,
  useGetDuplicateTargetCompanyIds,
} from "@lhd/hooks";
import { tableDescriptions } from "@lhd/features/Apps/AppList/utils";
import { AppTypes } from "@lhd/types";
import { ModalInner, WrapModal } from "./styled";
import { getCompanyId } from "@lhd/utility";
import { useParams } from "react-router-dom";
import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import { intersection } from "lodash";

export interface ModalShareRecordProps {
  open: boolean;
  onCancel: () => void;
  appId: string;
  selectedRecordIds: number[];
  onSuccess?: () => void | Promise<unknown>;
  appType: AppTypes;
  typeShare?: "multiple" | "single" | string;
}

const ModalShareRecord = ({
  open,
  onCancel,
  appId,
  selectedRecordIds,
  onSuccess,
  appType,
  typeShare,
}: ModalShareRecordProps) => {
  const { companyId: companyIdParam } = useParams();
  const companyId = Number(companyIdParam || getCompanyId());
  const { mutateAsync: duplicateRecordsAsync, isLoading: isDuplicating } =
    useDuplicateRecordsV2({ appType, appId, companyId });
  const idPrams = appType === APP_TYPES.COMPANY_POLICIES ? appId : companyId;
  const { data, isLoading, isFetching } = useAvailableDuplicateTargetCompanyIds(
    appType,
    idPrams,
    open,
  );

  const [searchName, setSearchName] = useState("");
  const [showPopupConfirm, setShowPopupConfirm] = useState(false);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);

  const {
    data: duplicateTargetCompanyIds,
    isLoading: isLoadingGetDuplicateTargetCompanyIds,
    isRefetching: isRefetchingGetDuplicateTargetCompanyIds,
    refetch: refetchDuplicateTargetCompanyIdsAsync,
  } = useGetDuplicateTargetCompanyIds(appType, idPrams, open, {
    record_ids: selectedRecordIds,
  });

  const listCompanyIdsHasDuplicated = useMemo(() => {
    return (
      duplicateTargetCompanyIds?.results?.flatMap((item) => item.company_ids) ??
      []
    );
  }, [duplicateTargetCompanyIds]);

  const companies = useMemo(() => {
    return (
      data?.companies
        .filter((company) => company.id !== Number(getCompanyId()))
        .map((c) => ({
          id: c.id,
          name: c.name,
          ...(typeShare === "single"
            ? {
                disabled: listCompanyIdsHasDuplicated.includes(c.id),
                defaultChecked: listCompanyIdsHasDuplicated.includes(c.id),
              }
            : {}),
        })) || []
    );
  }, [data?.companies, listCompanyIdsHasDuplicated, typeShare]);

  const listCompaniesWidthSearch = useMemo(() => {
    return (
      companies.filter((company) =>
        company.name.toLowerCase().includes(searchName.toLowerCase()),
      ) || []
    );
  }, [companies, searchName, open]);

  const companiesWidthOutDisabled = useMemo(() => {
    return companies.filter((c) => !c.disabled) || [];
  }, [companies, open]);

  const allChecked =
    checkedIds?.length ===
    companies.filter((c) => (typeShare === "single" ? !c.disabled : true))
      ?.length;

  const indeterminate =
    checkedIds.length > 0 && !allChecked && companies.length > 0;

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      const allCompanyIds = listCompaniesWidthSearch
        .filter((c) => (typeShare === "single" ? !c.disabled : true))
        .map((c) => c.id);
      setCheckedIds(allCompanyIds);
    } else {
      setCheckedIds([]);
    }
  };

  const handleToggleOne = (companyId: number, checked: boolean) => {
    if (checked) {
      setCheckedIds([...checkedIds, companyId]);
    } else {
      setCheckedIds(checkedIds.filter((id) => id !== companyId));
    }
  };

  const handleSubmit = async (isOverWrite?: boolean) => {
    await duplicateRecordsAsync({
      payload: {
        record_ids: selectedRecordIds,
        target_company_ids: Array.from(checkedIds),
        ...(typeShare === "multiple"
          ? {
              duplicate_mode: isOverWrite ? "overwrite" : "skip",
            }
          : {}),
      },
      cb: () => {
        onSuccess?.();
        onCancel();
      },
    });
  };

  const handleOk = () => {
    const hasCopy = duplicateTargetCompanyIds?.results?.find(
      (item) =>
        item?.company_ids &&
        Array.isArray(item?.company_ids) &&
        item.company_ids.length > 0 &&
        intersection(checkedIds, item.company_ids).length > 0,
    );
    if (hasCopy) {
      setShowPopupConfirm(true);
    } else {
      handleSubmit();
    }
  };

  useEffect(() => {
    if (!open) {
      setSearchName("");
      handleToggleAll(false);
    } else {
      refetchDuplicateTargetCompanyIdsAsync();
    }

    return () => setShowPopupConfirm(false);
  }, [open]);

  const showModal = open && !!appId && selectedRecordIds.length > 0;

  const isShowPopupConfirm = showPopupConfirm && typeShare === "multiple";

  return (
    <WrapModal
      open={showModal}
      onCancel={onCancel}
      footer={null}
      width={isShowPopupConfirm ? 400 : 800}
      centered
      destroyOnClose
      closable
      maskClosable={!isDuplicating}
    >
      <Spin
        spinning={
          isLoading ||
          isFetching ||
          isDuplicating ||
          isDuplicating ||
          isLoadingGetDuplicateTargetCompanyIds ||
          isRefetchingGetDuplicateTargetCompanyIds
        }
      >
        {isShowPopupConfirm ? (
          <ModalInner>
            <h2 className="modal-share-title">
              コピー済みの行が含まれています。
            </h2>
            <p>
              コピー済みの行を上書きする場合は「上書き」、
              <br />
              コピー済みの行を無視する場合は「無視」
              <br /> を押してください。
            </p>

            <div className="modal-share-footer">
              <Button
                type="primary"
                label="上書き"
                onClick={() => handleSubmit(true)}
              />
              <Button
                type="primary"
                label="無視"
                onClick={() => handleSubmit(false)}
              />
            </div>
          </ModalInner>
        ) : (
          <ModalInner>
            <h2 className="modal-share-title">他社へコピー（別レコード）</h2>

            <ul className="modal-share-bullets">
              {tableDescriptions.copy.map((html, i) => (
                <li key={i}>
                  <SafeHtml html={html} />
                </li>
              ))}
            </ul>
            <div></div>
            <div style={{ marginBottom: 12 }}>
              <Input.Search
                placeholder="会社名で検索"
                value={searchName}
                onChange={(e) => {
                  setSearchName(e.target.value);
                }}
              />
            </div>

            <div className="modal-share-checkboxes">
              <div className="modal-share-row">
                <Checkbox
                  checked={allChecked}
                  indeterminate={indeterminate}
                  disabled={!companies.length}
                  onChange={(e) =>
                    handleToggleAll(e.target.checked && !indeterminate)
                  }
                >
                  全社
                </Checkbox>
              </div>
              {listCompaniesWidthSearch.map((c) => (
                <div
                  key={c.id}
                  className="modal-share-row modal-share-row--indent"
                >
                  <Checkbox
                    checked={checkedIds.includes(c.id)}
                    onChange={(e) => handleToggleOne(c.id, e.target.checked)}
                    disabled={typeShare === "single" ? c.disabled : false}
                  >
                    {c.name}
                  </Checkbox>
                </div>
              ))}
            </div>

            <div className="modal-share-footer">
              <Button
                type="primary"
                label="コピー送信"
                loading={isDuplicating}
                disabled={!companies.length || checkedIds.length === 0}
                onClick={handleOk}
              />
              <Button
                className="btn-back"
                type="back"
                disabled={isDuplicating}
                onClick={onCancel}
                label="戻る"
              />
            </div>
          </ModalInner>
        )}
      </Spin>
    </WrapModal>
  );
};

export default ModalShareRecord;
